import { View, ImageScaleView } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import Scroll from 'src/game/components/shared/Scroll';
import bitmapFonts from 'src/lib/bitmapFonts';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import uiConfig from 'src/lib/ui/config';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import StateObserver from 'src/StateObserver';
import { CardId } from 'src/replicant/cards/cards.ruleset';
import { cardsGetFriends } from '../cards.friends';
import { withLoading } from 'src/lib/utils';
import { CardsFriend } from 'src/replicant/cards/cards.asyncGetters';

import { AssetGroup } from '@play-co/timestep-core';

export default class PopupCardSendList extends PopupBasic<
  (sent?: boolean) => void
> {
  private scroll: Scroll<CardsFriend>;
  private cardId!: CardId;

  constructor(opts: { superview: View; close: (sent?: boolean) => void }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: 582,
      height: 720,
      darkerBg: true,
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.REQUEST,
      },
    });

    const scrollWidth = 535;
    this.scroll = new Scroll<CardsFriend>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      firstItemOffset: 5,
      margin: 7,
      rect: {
        x: (this.box.style.width - scrollWidth) * 0.5,
        y: 80,
        width: scrollWidth,
        height: 600,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup(['assets/ui/shared/frames/scroller_panel_blue.png']);
  }

  private createItem(superview: View, _: number, item: CardsFriend) {
    const width = 510;
    const height = 95;

    const view = new View({
      tag: 'item',
      superview,
      width,
      height,
    });

    new ImageScaleView({
      tag: 'frame',
      superview: view,
      image: 'assets/ui/shared/frames/scroller_panel_blue.png',
      scaleMethod: '9slice',
      width,
      height,
      x: (superview.style.width - width) * 0.5,
      sourceSlices: {
        vertical: { top: 35, bottom: 35 },
        horizontal: { left: 35, right: 35 },
      },
    });

    new Avatar({
      superview: view,
      x: 32,
      y: 10,
      width: 68,
      height: 68,
      centerOnOrigin: false,
      image: item.photo,
      frame: item.frame,
    });

    let labelWidth = 225;

    new LangBitmapFontTextView({
      tag: 'friend-name',
      superview: view,
      align: 'left',
      verticalAlign: 'center',
      color: '#9A7171',
      wordWrap: false,
      x: 108,
      y: 25,
      size: 28,
      width: labelWidth,
      height: 35,
      font: bitmapFonts('Body'),
      localeText: () => item.name ?? '',
      letterSpacing: -2,
    });

    const button = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: -3,
      labelOffsetY: -2,
      x: 510 - 130,
      y: 12,
      width: 124,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('cards.cardSend.donate'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await StateObserver.invoke.cardsSendCard({
          cardId: this.cardId,
          userId: item.id,
          source: 'donate',
          sendMethod: 'request',
        });
        this.opts.close(true);
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.REQUEST,
      },
    });
    view.addSubview(button);

    return view;
  }

  public async init({ cardId }: { cardId: CardId }) {
    super.init({
      title: i18n('cards.cardSend.title'),
    });

    this.cardId = cardId;

    withLoading(() => cardsGetFriends(), {
      feature: Feature.CARDS._,
      subFeature: Feature.CARDS.GET_FRIENDS,
    }).then((friends) => {
      this.scroll.setItems(Object.values(friends));
    });
  }
}
